/**
 * * Centralized app routes following the aggregate pattern.
 *
 * * Each feature directory within app/ (e.g., dashboard, settings, auth) exports its own route
 * * object — e.g. `dashboardRoutes`, `settingsRoutes` — which are imported here
 * * and merged under their respective keys. This keeps route definitions modular
 * * and maintainable.
 *
 * * Example:
 * * import { dashboardRoutes } from '@/pages/dashboard/dashboardRoutes';
 * *
 * * export const appRoutes = {
 * *   home: '/',
 * *   dashboard: dashboardRoutes,
 * * } as const;
 *
 * * Usage:
 * * <Link href={appRoutes.dashboard.root}>Dashboard</Link>
 *
 * ! Note: Each feature route object must define an 'index' property.
 */

import { workspaceRoutes } from '@/app/(protected)/[workspace_slug]/routes';
import { onboardingRoutes } from '@/app/(protected)/onboarding/routes';
import { workspaceSelectRoutes } from '@/app/(protected)/workspace-select/routes';
import { authRoutes } from '@/app/(public)/auth/routes';
import { publicLegalRoutes } from '@/app/(public)/legal/routes';

export const appRoutes = {
  home: '/',
  auth: authRoutes,
  legal: publicLegalRoutes,
  workspaceSelect: workspaceSelectRoutes,
  workspace: workspaceRoutes,
  onboarding: onboardingRoutes,
} as const;

type ValueOf<T> = T[keyof T];
type NestedValue<T> = T extends string ? T : NestedValue<ValueOf<T>>;

export type AppRouteValue = NestedValue<typeof appRoutes>;
